import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { toast } from 'sonner';

import type { ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { MY_PERFORMANCE_PATH } from '@/modules/analytics/constants';
import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { useHumanAgentsEffectiveness } from './useHumanAgentsEffectiveness';
import { useHumanAgentsEffectivenessTimeseries } from './useHumanAgentsEffectivenessTimeseries';
import { useHumanAgentsOverview } from './useHumanAgentsOverview';
import { useHumanAgentsPerformance } from './useHumanAgentsPerformance';
import { useHumanAgentsTimeseries } from './useHumanAgentsTimeseries';

type WidgetId = 'human_agents_overview' | 'human_agents_effectiveness' | 'human_agents_performance';

export const useHumanAgentsExport = () => {
  const { t } = useTranslation();
  // My performance has no per-agent table, so skip that query and leave it out of the export.
  const includePerformance = !useLocation().pathname.includes(`/${MY_PERFORMANCE_PATH}`);
  const [isExporting, setIsExporting] = useState(false);

  const { data: overview } = useHumanAgentsOverview();
  const { data: overviewTimeseries } = useHumanAgentsTimeseries();
  const { data: effectiveness } = useHumanAgentsEffectiveness();
  const { data: effectivenessTimeseries } = useHumanAgentsEffectivenessTimeseries();
  const { data: performance } = useHumanAgentsPerformance({ skip: !includePerformance });

  const overviewPayload = useMemo(() => {
    const hasKpi =
      !!overview &&
      (!!overview.total_conversations ||
        overview.avg_csat_human_agent != null ||
        overview.avg_user_rating != null ||
        overview.avg_resolution_seconds != null);
    if (!hasKpi && overviewTimeseries.length === 0) {
      return null;
    }
    return {
      kpi: overview
        ? {
            total_conversations: overview.total_conversations,
            avg_resolution_seconds: overview.avg_resolution_seconds,
            avg_user_csat: overview.avg_user_rating,
            user_csat_reviews: overview.user_rating_count,
            avg_ai_csat: overview.avg_csat_human_agent,
            ai_csat_reviews: overview.csat_human_agent_count,
          }
        : null,
      timeseries: overviewTimeseries.map((point) => ({
        date: point.date,
        total_conversations: point.conversations,
        avg_resolution_seconds: point.resolutionTimeSeconds,
        user_csat: point.userCsat,
        user_csat_reviews: point.userReviews,
        ai_csat: point.aiCsat,
        ai_csat_reviews: point.aiReviews,
      })),
    };
  }, [overview, overviewTimeseries]);

  const effectivenessPayload = useMemo(() => {
    const hasKpi =
      !!effectiveness &&
      Object.entries(effectiveness).some(([key, v]) => key !== '__typename' && v != null);
    if (!hasKpi) {
      return null;
    }
    return { kpi: effectiveness, timeseries: effectivenessTimeseries };
  }, [effectiveness, effectivenessTimeseries]);

  const performancePayload = useMemo(() => {
    if (!includePerformance || performance.length === 0) {
      return null;
    }
    return performance.map(
      ({
        id,
        name,
        sessions,
        handlingSeconds,
        responseSeconds,
        resolutionSeconds,
        userCsat,
        aiCsat,
      }) => ({
        agent_id: id,
        agent_name: name,
        sessions,
        avg_handling_seconds: handlingSeconds,
        avg_response_seconds: responseSeconds,
        avg_resolution_seconds: resolutionSeconds,
        avg_user_csat: userCsat,
        avg_ai_csat: aiCsat,
      })
    );
  }, [performance, includePerformance]);

  const isWidgetExportable = useCallback(
    (widgetId: WidgetId) => {
      switch (widgetId) {
        case 'human_agents_overview':
          return overviewPayload !== null;
        case 'human_agents_effectiveness':
          return effectivenessPayload !== null;
        case 'human_agents_performance':
          return performancePayload !== null;
        default:
          return false;
      }
    },
    [overviewPayload, effectivenessPayload, performancePayload]
  );

  const isAnyExportable =
    overviewPayload !== null || effectivenessPayload !== null || performancePayload !== null;

  const flattenOverviewForCsv = useCallback((): Record<string, unknown>[] => {
    if (!overviewPayload) {
      return [];
    }
    const rows: Record<string, unknown>[] = [];
    if (overviewPayload.kpi) {
      rows.push({ row_type: 'kpi', ...overviewPayload.kpi });
    }
    for (const point of overviewPayload.timeseries) {
      rows.push({ row_type: 'timeseries', ...point });
    }
    return rows;
  }, [overviewPayload]);

  const flattenEffectivenessForCsv = useCallback((): Record<string, unknown>[] => {
    if (!effectivenessPayload) {
      return [];
    }
    const rows: Record<string, unknown>[] = [];
    if (effectivenessPayload.kpi) {
      rows.push({ row_type: 'kpi', ...effectivenessPayload.kpi });
    }
    for (const point of effectivenessPayload.timeseries) {
      rows.push({ row_type: 'timeseries', ...point });
    }
    return rows;
  }, [effectivenessPayload]);

  const exportWidget = useCallback(
    (widgetId: WidgetId, format: ExportFormat) => {
      try {
        setIsExporting(true);
        trackAnalyticsExport(PageName.ANALYTICS_HUMAN_AGENTS, widgetId, format);
        const filename = buildExportFilename(widgetId);

        if (format === 'json') {
          let data: unknown = null;
          switch (widgetId) {
            case 'human_agents_overview':
              data = overviewPayload;
              break;
            case 'human_agents_effectiveness':
              data = effectivenessPayload;
              break;
            case 'human_agents_performance':
              data = performancePayload;
              break;
          }
          if (data) {
            downloadJson(data, filename);
          }
          return;
        }

        let rows: Record<string, unknown>[] = [];
        switch (widgetId) {
          case 'human_agents_overview':
            rows = flattenOverviewForCsv();
            break;
          case 'human_agents_effectiveness':
            rows = flattenEffectivenessForCsv();
            break;
          case 'human_agents_performance':
            rows = performancePayload ?? [];
            break;
        }
        if (rows.length > 0) {
          downloadCsv(rows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [
      overviewPayload,
      effectivenessPayload,
      performancePayload,
      flattenOverviewForCsv,
      flattenEffectivenessForCsv,
      t,
    ]
  );

  const exportAll = useCallback(
    (format: ExportFormat) => {
      try {
        setIsExporting(true);
        trackAnalyticsExport(PageName.ANALYTICS_HUMAN_AGENTS, 'human_agents_all', format);
        const filename = buildExportFilename('human_agents_all');

        if (format === 'json') {
          const merged: Record<string, unknown> = {};
          if (overviewPayload) {
            merged.human_agents_overview = overviewPayload;
          }
          if (effectivenessPayload) {
            merged.human_agents_effectiveness = effectivenessPayload;
          }
          if (performancePayload) {
            merged.human_agents_performance = performancePayload;
          }
          if (Object.keys(merged).length > 0) {
            downloadJson(merged, filename);
          }
          return;
        }

        const allRows: Record<string, unknown>[] = [];
        for (const row of flattenOverviewForCsv()) {
          allRows.push({ widget: 'human_agents_overview', ...row });
        }
        for (const row of flattenEffectivenessForCsv()) {
          allRows.push({ widget: 'human_agents_effectiveness', ...row });
        }
        for (const row of performancePayload ?? []) {
          allRows.push({ widget: 'human_agents_performance', ...row });
        }
        if (allRows.length > 0) {
          downloadCsv(allRows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [
      overviewPayload,
      effectivenessPayload,
      performancePayload,
      flattenOverviewForCsv,
      flattenEffectivenessForCsv,
      t,
    ]
  );

  return { exportWidget, exportAll, isWidgetExportable, isAnyExportable, isExporting };
};
